import React, { useState, useEffect, useRef } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import AreaInfo from "../components/analytics/AreaInfo";
import Graphs from "../components/analytics/Graphs";
import SatelliteImage from "../components/analytics/SatelliteImage";
import styles from "./AnalyticsDashboard.module.css";
import { useAnalysis } from "../contexts/AnalysisContext";
import EventDates from "../components/analytics/AvailableDates.js";
import { DateSelectionProvider } from "../contexts/DateSelectionProvider";
import Modal from "../components/Modal/Modal.js";
import CalendarSmallScreen from "../components/analytics/CalanderForSmallScreen.js";
import { FieldProvider } from "../contexts/FieldContext.js";
import axios from "axios";
import WeatherForecast from "../components/analytics/WeatherForecast.js";
import ReportModal from "../components/analytics/ReportModal.js";
import { urls } from "../services/apiService.js"
import OpacitySlider from "../components/OpacityButton/OpacitySlider.js";
import { useTranslation } from "react-i18next";
import { FaTools } from "react-icons/fa"; // Maintenance icon
import { checkPageStatus } from "../utilityhook/pagecheck"; // ✅ import utility
import Swal from 'sweetalert2';

const AnalyticsDashboard = ({ userEmail }) => {
  const {
    fetchResults,
    flag,
    setAnalysisType,
    polygon,
    setPolygon,
    runNewAnalysis,
    fetchIndicesValues,
    setToastMsg,
    toastMsg,
    handleCloseToast,
    showDates,
    setShowDates,
  } = useAnalysis();
  let coordinatesArray;
  const [selectedOption, setSelectedOption] = useState("NDVI");
  const [startDate, setStartDate] = useState("Invalid Date");
  const [endDate, setEndDate] = useState("Invalid Date");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("Analyzing process not started");
  const [selectedField, setSelectedField] = useState("");
  const [fields, setFields] = useState([]);
  const [savedCoordinates, setSavedCoordinates] = useState([]);
  const [reloadButton, setReloadButton] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null); // Reference to the menu
  const [pFlag, setPFlag] = useState(false)
  const username = localStorage.getItem("User");
  const userPlan = localStorage.getItem("userPlan");
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(true); // ✅ track maintenance status
 const [selectedDate, setSelectedDate] = useState(() => JSON.parse(localStorage.getItem('SelectedDate')) || '');
 const [showNotifications, setShowNotifications] = useState(false);
const [unreadCount, setUnreadCount] = useState(3);
const [notifications, setNotifications] = useState([
  { id: 1, title: "Analysis Complete", message: "Your analysis has been completed successfully!", read: false, time: "2 min ago" },
  { id: 2, title: "New Satellite Imagery", message: "New satellite imagery is now available for your fields", read: false, time: "1 hour ago" },
  { id: 3, title: "Weather Alert", message: "Heavy rain expected in your area. Take necessary precautions.", read: false, time: "3 hours ago" }
]);
const [weatherForecast, setWeatherForecast] = useState([]);
const [weatherHistory, setWeatherHistory] = useState([]);
const [weatherLoading, setWeatherLoading] = useState(false);
const [locationForecast, setLocationForecast] = useState(null);
const [currentWeatherData, setCurrentWeatherData] = useState(null);

// Add this function inside your AnalyticsDashboard component
const fetchWeatherForecast = async (lat, lon) => {
  try {
    const res = await axios.get(
      `https://api.weatherapi.com/v1/forecast.json?key=${API_KEY}&q=${lat},${lon}&days=14`
    );
    setWeatherForecast(res.data.forecast.forecastday);
    setLocationForecast(res.data.location);
    setCurrentWeatherData(res.data.current);
    localStorage.setItem("forecast", JSON.stringify(res.data.forecast.forecastday));
    return res.data.forecast.forecastday;
  } catch (err) {
    console.error("Error fetching forecast:", err);
    return [];
  }
};

const fetchWeatherHistory = async (lat, lon) => {
  try {
    let sowingDateStr = localStorage.getItem("sowingDate");
    let sowingDate;
    if (!sowingDateStr) {
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() - 14);
      sowingDate = fallbackDate;
    } else {
      sowingDate = new Date(sowingDateStr);
    }

    const today = new Date();
    const dayCount = Math.ceil((today - sowingDate) / (1000 * 60 * 60 * 24));

    // Prepare all promises first
    const requests = [];
    for (let i = 0; i <= dayCount; i++) {
      const current = new Date(sowingDate);
      current.setDate(sowingDate.getDate() + i);
      const formattedDate = current.toISOString().split("T")[0];

      requests.push(
        axios
          .get(
            `https://api.weatherapi.com/v1/history.json?key=${API_KEY}&q=${lat},${lon}&dt=${formattedDate}`
          )
          .then((response) => response.data?.forecast?.forecastday?.[0])
          .catch((err) => {
            console.error("Error for", formattedDate, err.message);
            return null;
          })
      );
    }

    // Run all requests concurrently
    const results = await Promise.all(requests);
    const validResults = results.filter(Boolean);

    if (validResults.length > 0) {
      setWeatherHistory(validResults);
      return validResults;
    } else {
      console.warn("No historical data found.");
      setWeatherHistory([]);
      return [];
    }
  } catch (err) {
    console.error("Error fetching history:", err);
    return [];
  }
};

// Add this useEffect to fetch weather data when coordinates change
useEffect(() => {
  const fetchAllWeatherData = async () => {
    if (!isActive) return;
    
    setWeatherLoading(true);
    try {
      // Get coordinates
      let lat = 24.8607;
      let lon = 67.0011;
      
      if (savedCoordinates.length > 0) {
        lat = savedCoordinates[0][1];
        lon = savedCoordinates[0][0];
      }
      
      // Fetch both forecast and history in parallel
      const [forecastData, historyData] = await Promise.all([
        fetchWeatherForecast(lat, lon),
        fetchWeatherHistory(lat, lon)
      ]);
      
      console.log("Weather data fetched successfully:", { forecastData, historyData });
    } catch (error) {
      console.error("Error fetching weather data:", error);
    } finally {
      setWeatherLoading(false);
    }
  };
  
  fetchAllWeatherData();
}, [savedCoordinates, isActive, selectedField]);
const notificationPanelRef = useRef(null);

// Function to show SweetAlert popup
const showNotificationPopup = (notification) => {
  Swal.fire({
    title: notification.title,
    text: notification.message,
    icon: 'info',
    timer: 5000,
    timerProgressBar: true,
    showConfirmButton: false,
    showCloseButton: true,
    position: 'center',
    background: '#fff',
    customClass: {
      popup: 'rounded-xl shadow-2xl',
      title: 'text-lg font-semibold text-gray-800',
      htmlContainer: 'text-gray-600'
    },
    didOpen: () => {
      // Mark notification as read when popup opens
      markAsRead(notification.id);
    }
  });
};

const toggleNotifications = () => {
  if (!isActive) return;
  // Just toggle the dropdown - no auto-close timer
  setShowNotifications(!showNotifications);
};

const handleNotificationClick = (notification) => {
  // Don't close the dropdown immediately - let the popup open while dropdown stays open
  // But we'll mark it as read
  if (!notification.read) {
    markAsRead(notification.id);
  }
  
  // Show SweetAlert popup
  showNotificationPopup(notification);
  
  // Optional: You can choose to keep the dropdown open or close it
  // To keep it open, comment out the line below
  // setShowNotifications(false);
};

const markAsRead = (id) => {
  setNotifications(prev => prev.map(notif => 
    notif.id === id ? { ...notif, read: true } : notif
  ));
  setUnreadCount(prev => Math.max(0, prev - 1));
};

const markAllAsRead = () => {
  setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
  setUnreadCount(0);
};

const closeNotifications = () => {
  setShowNotifications(false);
};

// Close dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (event) => {
    // Check if click is outside the notification panel AND outside the bell button
    if (notificationPanelRef.current && 
        !notificationPanelRef.current.contains(event.target) &&
        !event.target.closest('.notification-bell')) {
      setShowNotifications(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []);
 
  useEffect(() => {
    const fetchPageStatus = async () => {
      const active = await checkPageStatus("2-analytics-dashboard"); // ✅ wait for backend or localStorage
      setIsActive(active);
    };
    fetchPageStatus();
  }, []);


  useEffect(() => {
    // Convert both the username and the restrictedUsers to lowercase for case-insensitive comparison
    if (userPlan === "Free" || !userPlan) {
      setPFlag(true); // Set pFlag to true if user is restricted
    }
    else {
      setPFlag(false);
    }
  }, [username, userPlan]); // Run the effect when username changes

  useEffect(() => {
    const handleMouseClick = (event) => {
      // Close the menu only if the click is outside the menu
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleMouseClick);

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleMouseClick);
    };
  }, []);

  const toggleMenu = (event) => {
    if (!isActive) return;
    event.stopPropagation(); // Prevent the event from propagating to the document
    setIsMenuOpen((prev) => !prev);
  };

  const openModal = () => {
    if (!isActive) return;
    setModalVisible(true);
  };

  const closeModal = () => {
    if (!isActive) return;
    setModalVisible(false);
  };
  // weather history date fetching from local storage
  useEffect(() => {
  const interval = setInterval(() => {
    const stored = JSON.parse(localStorage.getItem('SelectedDate'));

    if (stored) {
      // assuming stored format = "DD-MM-YYYY"
      const [day, month, year] = stored.split("-");
      const formattedDate = `${year}-${month}-${day}`;

      // update only if changed
      if (formattedDate !== selectedDate) {
        setSelectedDate(formattedDate);
      }
    }
  }, 1000);

  return () => clearInterval(interval);
}, [selectedDate]);

console.log("Selected date in AnalyticsDashboard:", selectedDate);


  const [weatherData, setWeatherData] = useState({
    temperature: null,
    humidity: null,
    precipitation: null,
  });

  useEffect(() => {
    // Check if savedCoordinates exist or if "data" keyword is in local storage
    const storedData = localStorage.getItem("data");
    localStorage.removeItem("placemarkReport");
    setHasData(savedCoordinates.length > 0 || storedData);
  }, [savedCoordinates]);


  useEffect(() => {
    setAnalysisType(selectedOption);
  }, [selectedOption, setAnalysisType]);

  useEffect(() => {
    if (!isActive) return;

    if (userEmail) {
      fetch(
        `${urls.get_fieldnames}?username=${encodeURIComponent(
          userEmail
        )}`
      )
        .then((response) => response.json())
        .then((data) => {

          // console.log("Fetched data:", obj);
          setFields(data.fields || []);
        })
        .catch((error) => console.error("Error fetching field names:", error));
    } else {
      console.error("User email is not available.");
    }
  }, [userEmail, isActive]);


  useEffect(() => {
    if (!isActive) return;

    fetchResults(selectedField, selectedOption);
    fetchIndicesValues(selectedField);

  }, [selectedField, selectedOption, isActive]);



  const satpayload = {
    fieldName: selectedField,
    coordinates: savedCoordinates,
  };

  const coordinates = (value) => {
    if (!isActive) return;

    if (value.length === 0) {
      setToastMsg({
        message: "Coordinates are not available. Select field again",
        type: "error",
        isShown: true,
      });
    } else {
      setPolygon(value);
    }
  };

  const date = (objDate) => {
    if (!isActive) return;

    const startDate = objDate.startDate.toString();
    const endDate = objDate.endDate.toString();
    if (startDate !== "Invalid Date" || endDate !== "Invalid Date") {
      setStartDate(startDate);
      setEndDate(endDate);
    }
  };

  const handleDoAnalysis = async (polygon) => {
    if (!isActive) return;

    setIsLoading(true);
    setError("analysing");
    setShowDates(false);

    // Check localStorage for existing data first
    const selRes = localStorage.getItem("data");
    if (selRes) {
      try {
        const selResJson = JSON.parse(selRes);
        const { coordinates, area, fieldName ,cropName} = selResJson;

        // If valid data is found in localStorage, use it
        if (coordinates && area && fieldName) {
          // console.log("Using data from localStorage");

          // Convert coordinates to the required format for the API
          const output = coordinates.map((coord) => [
            coord.longitude,
            coord.latitude,
          ]);


          // Reset previous analysis
          runNewAnalysis();

          // Trigger batch processing with localStorage data
          await triggerBatchProcessing({ output, fieldName ,cropName});

          // Clear localStorage after analysis
          // localStorage.removeItem("data");
          return; // Exit after processing localStorage data
        }
      } catch (err) {
        console.error("Error parsing localStorage data:", err);
        setError("Failed to use stored data for analysis.");
        setIsLoading(false);
        return;
      }
    }

    // If no valid localStorage data, check polygon data
    if (!polygon) {
      setError("No polygon data available.");
      setIsLoading(false);
      return;
    }

    // Reset previous analysis
    runNewAnalysis();

    // Trigger batch processing with provided polygon data
    await triggerBatchProcessing(polygon);
  };

  const triggerBatchProcessing = async ({ output, fieldName, cropName}) => {
    if (!isActive) return;

    setIsLoading(true);

    // 🧹 Step 1: Remove any existing record for this field name from localStorage
    updateFieldByNameFromLocalStorage(fieldName);

    // 🖇️ Step 1.5: Update only the source in localStorage
    const existingData = JSON.parse(localStorage.getItem("data")) || {};
    existingData.source = "cardPress";
    localStorage.setItem("data", JSON.stringify(existingData));


    // 🛰️ Step 2: Proceed with batch request
    const batchApiUrl = urls.start_batch;
    const payload = {
      fieldName,
      cropName,
      coordinates: output,
      start_date: startDate,
      end_date: endDate,
      analysis_types: ["NDVI", "NDMI", "MSAVI", "NDRE", "NCC"],
      user_email: userEmail,
    };

    try {
      const response = await fetch(batchApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to start analysis batch");

      const result = await response.json();
      setError("Analyzing Completed.");
      setReloadButton(true);
    } catch (error) {
      console.error("Error starting batch processing:", error);
      setError(error.message || "An error occurred during batch processing");
    } finally {
      setIsLoading(false);
    }
  };


  // Function to mark a field by name as needing refetch in localStorage
  const updateFieldByNameFromLocalStorage = (fieldName) => {
    try {
      const stored = JSON.parse(localStorage.getItem("fieldLastDates")) || [];

      const updated = stored.map((item) => {
        if (item.fieldName?.toLowerCase() === fieldName?.toLowerCase()) {
          return {
            ...item,
            shouldRefetch: true, // ensure the flag is set
          };
        }
        return item;
      });

      // Check if the field exists but doesn't have the flag yet
      const exists = stored.some(
        (item) => item.fieldName?.toLowerCase() === fieldName?.toLowerCase()
      );

      if (!exists) {
        console.warn(`⚠️ Field "${fieldName}" not found in localStorage.`);
      }

      localStorage.setItem("fieldLastDates", JSON.stringify(updated));
      console.log(`✅ Marked "${fieldName}" as shouldRefetch = true in localStorage.`);
    } catch (err) {
      console.error("Error updating field flag in localStorage:", err);
    }
  };


  useEffect(() => {
    if (!isActive) return;

    const selRes = localStorage.getItem("data");
    if (!selRes) return;

    try {
      const selResJson = JSON.parse(selRes);
      const { coordinates, area, fieldName, source } = selResJson;

      if (!coordinates || !area || !fieldName) return;

      // Only proceed *after* fields are loaded
      if (fields.length === 0) {
        console.log("Fields not loaded yet, delaying field check...");
        return;
      }

      const output = coordinates.map((coord) => [
        coord.longitude,
        coord.latitude,
      ]);

      // ✅ Handle CHART CLICK
      if (source === "chartClick") {
        console.log("ChartClick detected → showing date popup + background field");

        // Show the date popup
        setShowDates(true);

        // Try to show the corresponding field behind the popup
        const matchingField = fields.find((f) => f.fieldname === fieldName);

        if (matchingField) {
          try {
            const coordsArray = JSON.parse(matchingField.coordinates);
            const transformed = coordsArray.map((pair) => [
              Number(pair[0]),
              Number(pair[1]),
            ]);

            setSelectedField(fieldName);
            setSavedCoordinates(transformed);
            setError(`Analyse New Data For: ${matchingField.fieldname}`);
          } catch (err) {
            console.error("Error parsing coordinates for chart click:", err);
          }
        } else {
          console.log(`Field "${fieldName}" not found yet, retrying for chartClick...`);
          setTimeout(() => {
            const retryMatch = fields.find((f) => f.fieldname === fieldName);
            if (retryMatch) {
              try {
                const coordsArray = JSON.parse(retryMatch.coordinates);
                const transformed = coordsArray.map((pair) => [
                  Number(pair[0]),
                  Number(pair[1]),
                ]);
                setSelectedField(fieldName);
                setSavedCoordinates(transformed);
                setError(`Analysing For: ${retryMatch.fieldname}`);
              } catch (err) {
                console.error("Error parsing retried chartClick coordinates:", err);
              }
            }
          }, 2000);
        }

        // 🧠 Important: stop here — don't continue to cardPress logic
        return;
      }

      // ✅ Handle CARD PRESS
      if (source === "cardPress") {
        const matchingField = fields.find((f) => f.fieldname === fieldName);

        if (matchingField) {
          try {
            const coordsArray = JSON.parse(matchingField.coordinates);
            const transformed = coordsArray.map((pair) => [
              Number(pair[0]),
              Number(pair[1]),
            ]);

            setSelectedField(fieldName);
            setSavedCoordinates(transformed);
            setError(`Analysed Data For: ${matchingField.fieldname}`);
            setShowDates(false);
          } catch (err) {
            console.error("Error parsing stored field coordinates:", err);
          }
        } else {
          console.log(`Field "${fieldName}" not found yet, retrying...`);
          setTimeout(() => {
            const retryMatch = fields.find((f) => f.fieldname === fieldName);
            if (retryMatch) {
              try {
                const coordsArray = JSON.parse(retryMatch.coordinates);
                const transformed = coordsArray.map((pair) => [
                  Number(pair[0]),
                  Number(pair[1]),
                ]);

                setSelectedField(fieldName);
                setSavedCoordinates(transformed);
                setError(`Analysing Done For: ${retryMatch.fieldname}`);
                setShowDates(false);
              } catch (err) {
                console.error("Error parsing retried coordinates:", err);
              }
            } else {
              // ✅ If still not found → open date picker for fresh analysis
              setShowDates(true);
            }
          }, 2000);
        }
      }
    } catch (err) {
      console.error("Error parsing localStorage data:", err);
    }
  }, [fields, isActive]);





  // handling the reload button
  const handleReloadButton = () => {
    if (!isActive) return;
    window.location.reload();
    setReloadButton(false);
  };

  const handleClose = () => {
    if (!isActive) return;
    setShowDates(false);
    // localStorage.removeItem("data");
  };

  const API_KEY = "efb3685cfdd64117aa0102758242911"; // Your API key
  var lat = 24.8607;
  var lon = 67.0011;
  if (savedCoordinates.length > 0) {
    lat = savedCoordinates[0][1]; // Example latitude (replace with your coordinates)
    lon = savedCoordinates[0][0]; // Example longitude (replace with your coordinates)
  }

  useEffect(() => {
    if (!isActive) return;

    const fetchWeatherData = async () => {
      try {
        console.log("📡 weather data for coordinates:", { lat, lon, selectedDate,API_KEY });
        const response = await axios.get(
          `https://api.weatherapi.com/v1/history.json?key=${API_KEY}&q=${lat},${lon}&dt=${selectedDate}`
        );
        const data = response.data.forecast.forecastday[0].day;

        console.log("The history weather data is :", data);
        // ✅ Save weather in React state for UI display
        const weather = {
          temperature: data.avgtemp_c,
          humidity: data.avghumidity,
          precipitation: data.totalprecip_mm,
        };
        setWeatherData(weather);

        // ✅ Save full Weather_Data in localStorage exactly as before
        localStorage.setItem("Weather_Data", JSON.stringify(data));

        // ✅ Optional: Also save to Flask backend
        await axios.post(urls.saveWeather, {
          latitude: lat,
          longitude: lon,
          ...weather,
        });

        console.log("✅ Weather data saved to localStorage and backend:", data);
      } catch (error) {
        console.error("❌ Error fetching weather data:", error);
      }
    };

    if (lat && lon) fetchWeatherData();
  }, [lat, lon, selectedField, isActive,selectedDate]);


  const handleOpenModal = () => {
    if (!isActive) return;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isActive) return;
    setIsModalOpen(false);
  };


  return (
    <FieldProvider>
      <div className="
  absolute 
  right-0  
  w-full 
  lg:w-[94%] 
  2xl:w-[94%] 
  md:h-screen 
  overflow-hidden 
  p-2 
  flex 
  flex-col 
  items-center 
  overflow-x-hidden 
  sm:overflow-y-auto
">
        {/* 🚧 Under Maintenance Overlay (only visible when inactive) */}
        {!isActive && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 bg-opacity-90 flex flex-col items-center justify-center text-center z-[9999] backdrop-blur-sm">
            <FaTools className="text-5xl text-green-500 mb-4 animate-bounce" />
            <h1 className="text-3xl md:text-5xl font-semibold text-gray-600 mb-2">
              Page Under Maintenance
            </h1>
            <p className="text-gray-500 text-lg max-w-md">
              We're currently enhancing this feature for better accuracy and
              user experience. Please check back soon!
            </p>
            <div className="mt-6">
              <div className="w-16 h-16 border-4 border-gray-600 border-t-green-500 rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        )}

        <DateSelectionProvider>
          {/* Top Bar */}
          <div className="flex flex-wrap items-center justify-between py-2 w-full gap-2">
            <div className="bg-[#dbeafe] p-4 rounded-md flex flex-col gap-2 items-center justify-end">
              <h1 className="text-sm sm:text-[16px] 2xl:text-lg font-medium mb-2 md:mb-0">
                <p>
                  <span className="font-semibold text-wrap">{t("Status")} :</span>
                  &nbsp;{t(error.split('-')[0])} {error === "analysing" ? <span className="animate-ping duration-[2000ms] text-bold text-2xl">...</span> : null}
                </p>
              </h1>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-2 sm:gap-3 justify-between w-full lg:w-[35%]">
              <div className="w-full flex items-center justify-between gap-2">
{/* Notification Bell Icon with Sliding Panel - Stays open until manually closed */}

<div className="relative">
  {/* Menu Icon - Left open dropdown style */}
  <button
    className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
    onClick={toggleMenu}
    ref={menuRef}
    disabled={!isActive}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="2"
      stroke="currentColor"
      className="w-5 h-5 text-gray-600"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  </button>

  {/* Dropdown Menu - Left aligned */}
  {isMenuOpen && (
    <div ref={menuRef} className="absolute top-full left-0 mt-2 w-64 bg-white shadow-lg rounded-md p-2 z-50">
      <div className="flex flex-col gap-2">
        {/* Generate Report Button with Icon */}
        <button
          className={`w-full flex items-center gap-3 px-4 py-2 rounded-md ${pFlag || !isActive ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-green-50 text-gray-700 hover:text-green-600 transition-colors duration-200'}`}
          onClick={handleOpenModal}
          disabled={pFlag || !isActive}
          title={pFlag ? t("Upgrade to Premium to access this feature") : !isActive ? "Feature unavailable during maintenance" : ""}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth="1.5" 
            stroke="currentColor" 
            className="w-5 h-5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 0 0-1.883 2.542l.857 6a2.25 2.25 0 0 0 2.227 1.932H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-1.883-2.542m-16.5 0V6A2.25 2.25 0 0 1 6 3.75h3.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 0 1.06.44H18A2.25 2.25 0 0 1 20.25 9v.776" />
          </svg>
          <span className="flex-1 text-left">{t("Generate Report")}</span>
        </button>
        
        {/* Opacity Slider Button with Icon */}
        <div className="w-full">
          <OpacitySlider />
        </div>
      </div>
    </div>
  )}
</div>



                <div
                  className="min-w-fit w-1/2 bg-green-500 text-white font-medium py-2 px-2 rounded-md text-center"
                >
                  {selectedField
                    ? selectedField.split('-')[0]
                    : t("No Field Selected")}
                </div>
                <select
                  className="w-1/2 bg-green-500 text-white font-medium py-2 px-2 rounded-md cursor-pointer hover:bg-green-600 duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-center"
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  disabled={!isActive}
                >
                  <option className="text-center" value="NDVI">{t("Crop Health")}</option>
                  <option className="text-center" value="NDRE">{t("Fertilizer Content")}</option>
                  <option className="text-center" value="NDMI">{t("Moisture Content")}</option>
                  <option className="text-center" value="MSAVI">{t("Germination & Initial Growth")}</option>
                  <option className="text-center" value="NCC">{t("Natural Image")}</option>
                </select>

              </div>
            </div>
          </div>
          <div className="mb-3 w-full h-[48vh] 2xl:h-[48vh]">
            {fields.length === 0 ? (
              <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-400">
                Loading saved fields...
              </div>
            ) : selectedField &&
              fields.some((f) => f.fieldname === selectedField) &&
              savedCoordinates.length > 0 ? (
              <SatelliteImage
                flag={flag}
                cord={satpayload}
                selectedIndex={selectedOption}
                className={styles.satelliteImage}
                showDates={showDates}
                setShowDates={setShowDates}
                selectedField={selectedField}
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-500 rounded-md">
                No saved field data found for this field.
              </div>
            )}

          </div>


      
            <WeatherForecast 
              savedCoordinates={savedCoordinates} 
              isVisible={isModalVisible} 
              onClose={closeModal}
              forecast={weatherForecast}
              history={weatherHistory}
              loading={weatherLoading}
              locationForecast={locationForecast}
              currentData={currentWeatherData}
            />
          {/* In AnalyticsDashboard component, replace the grid section with this: */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full items-stretch">
            {/* Left Card */}
            <div className="bg-[#dbeafe] p-4 sm:p-6 rounded-md flex flex-col gap-2 h-full justify-between">
              <EventDates option={selectedOption} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full h-full">
                <div className="flex flex-col sm:flex-row md:flex-col gap-2 w-full items-center border-[1.5px] border-slate-200 p-2 rounded-lg bg-white">
                  <button
                    className="w-full sm:w-32 md:w-full h-12 flex items-center justify-center text-lg font-medium bg-[#facc15] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowDates((prev) => !prev)}
                    disabled={!isActive}
                  >
                    <FaCalendarAlt size={22} />
                    <span className="hidden sm:inline-block ml-2">
                      {t("Select Date")}
                    </span>
                  </button>
                  <AreaInfo polygon={coordinates} />
                </div>

                <div className="w-full overflow-y-auto flex flex-col gap-2 p-2 bg-white rounded-md h-full">
                  {/* Weather data content remains the same */}
                  {hasData ? (
                    <>
                      <div className="w-full h-12 flex items-center justify-between bg-gray-200 rounded-md px-3">
                        {`${t("Temperature")}: ${weatherData.temperature}°C`}
                      </div>
                      <div className="w-full h-12 flex items-center justify-between bg-gray-200 rounded-md px-3">
                        {`${t("Humidity")}: ${weatherData.humidity}%`}
                      </div>
                      <div className="w-full h-12 flex items-center justify-between bg-gray-200 rounded-md px-3">
                        {`${t("Precipitation")}: ${weatherData.precipitation}mm`}
                      </div>
                      <div className="flex justify-center mt-auto">
                        <button onClick={openModal} className="text-xl w-40 text-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed" disabled={!isActive}>
                          {t("See more")}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h1 className="text-gray-500 font-bold">{t("Weather Parameter")}</h1>
                      <div className="text-red-500">
                        {t("Select field from saved field data button")}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
                
            {/* Right Graph - Updated for mobile */}
            <div className="w-full h-full">
              <Graphs selectedIndex={selectedOption} isLoading={isLoading} className={styles.graphs} />
            </div>
          </div>
          {/* Date sidebar */}
          <div
            className={`fixed top-0 right-0 w-full md:w-[500px] h-screen bg-white  md:p-4 z-50 duration-500 gap-4 ${showDates
              ? "translate-x-[0px] opacity-1 shadow-custom"
              : "translate-x-[900px] opacity-0 "
              }`}
          >
            {/* <Calendar className="" date={date} height={10} /> */}
            <CalendarSmallScreen date={date} />

            <div className="absolute top-4 left-2 flex gap-4 items-center">
              <div>
                {isLoading ? (
                  <button
                    type="button"
                    className="flex items-center bg-green-500 text-white font-medium py-2 px-4 rounded-md md:max-w-48"
                    disabled
                  >
                    <svg
                      className="animate-spin h-5 w-5 mr-3 text-white border-t-white border border-green-500 rounded-full"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
                      />
                    </svg>
                    {t("Processing...")}
                  </button>
                ) : (
                  <button
                    className="bg-cyan-500 text-white font-medium py-2 px-4 rounded-md lg:max-w-40 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleDoAnalysis(polygon)}
                    disabled={!isActive}
                  >
                    {t("Run Analysis")}
                  </button>
                )}
              </div>
              <span
                className="cursor-pointer bg-red-500 text-white p-2 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleClose}
                style={!isActive ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
              >
                {t("Close")}
              </span>
            </div>

          </div>


          {/*================================= report Modal =========================================== */}
          <ReportModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            field={selectedField}
            weatherData={weatherData}
          />
        </DateSelectionProvider>

        {/*================================= Notification Modal =========================================== */}
        <Modal
          message={toastMsg.message}
          onClose={handleCloseToast}
          type={toastMsg.type}
          isShown={toastMsg.isShown}
        />

        {/*================================= Reload Button Modal ========================================== */}
        <div
          className={`${reloadButton ? "flex" : "hidden"
            } items-center justify-center fixed top-0 left-0 right-0 w-full h-screen z-[500] bg-black bg-opacity-50`}
        >
          <div className="max-w-[90%] p-4 rounded-md bg-white py-10 flex flex-col items-center justify-center gap-2 text-center">
            <p className="text-green-500 font-semibold">
              {t("Analysis Completed Successfully")}
            </p>
            <p className="text-gray-400 text-sm font-medium">
              {t("Please press the button to reload the page and save your data in the field.")}
            </p>
            <button
              className="h-[50px] min-w-fit bg-green-500 hover:bg-green-600 hover:text-white transition-all text-white rounded w-[150px] px-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleReloadButton}
              disabled={!isActive}
            >
              {t("Save Field Data")}
            </button>
          </div>
        </div>
      </div>
    </FieldProvider>
  );
};

export default AnalyticsDashboard;